import React from 'react';
import { Typography, Box, Chip } from '@mui/material';
import { LocationOn as LocationIcon, Verified as VerifiedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getLanguageFlag } from '../../../utils/languageUtils';
import { Driver } from '../../../models/driver';
import { COUNTRY_NAMES } from '../../../models/country';

interface DriverCardProps {
  driver: Driver;
  onClick: (driver: Driver) => void;
}

const DriverCard: React.FC<DriverCardProps> = ({ driver, onClick }) => {
  const { t } = useTranslation();

  // Add safety checks for driver data
  if (!driver) {
    return null;
  }

  const getDriverStatus = (driver: Driver) => driver.isActive ? 'active' : 'inactive';

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'background.green', icon: '✓', label: t('Active') };
      case 'inactive':
        return { color: 'background.red', icon: '✗', label: t('Inactive') };
      default:
        return { color: 'background.darkGrey', icon: '?', label: t('Unknown') };
    }
  };

  const status = getDriverStatus(driver);
  const statusDisplay = getStatusDisplay(status);

  // Safe name handling
  const firstName = driver.firstName || '';
  const lastName = driver.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim() || t('Unknown Driver');

  // Get country name from code
  const countryName = driver.baseCountry
    ? (COUNTRY_NAMES[driver.baseCountry as keyof typeof COUNTRY_NAMES] || driver.baseCountry)
    : t('Unknown');

  return (
    <Box
      onClick={() => onClick(driver)}
      sx={{
        backgroundColor: 'transparent',
        borderRadius: 2,
        p: { xs: 2.5, sm: 3 },
        minHeight: { xs: 180, sm: 200 },
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid',
        borderColor: 'border.default',
        '&:hover': {
          borderColor: 'border.medium',
          backgroundColor: 'background.lightGrey',
        },
      }}
    >
      <Box sx={styles.cardContent}>
        <Box sx={styles.cardBody}>
          <Box sx={styles.modernDriverHeader}>
            <Box sx={styles.driverNameContainer}>
              <Typography sx={styles.modernDriverName}>
                {fullName}
              </Typography>
              <Typography sx={styles.driverId}>
                ID: {driver.id}
              </Typography>
            </Box>
            <Box sx={{
              ...styles.statusBadge,
              bgcolor: statusDisplay.color
            }}>
              <Typography sx={styles.statusText}>
                {statusDisplay.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={styles.locationSection}>
            <LocationIcon sx={styles.locationIcon} />
            <Box sx={styles.locationInfo}>
              <Typography sx={styles.locationText}>
                {countryName}
              </Typography>
              <Typography sx={styles.locationTime}>
                {driver.mainLocation &&
                  typeof driver.mainLocation.latitude === 'number' &&
                  typeof driver.mainLocation.longitude === 'number' &&
                  driver.mainLocation.latitude !== 0 &&
                  driver.mainLocation.longitude !== 0 ?
                  `${driver.mainLocation.latitude}, ${driver.mainLocation.longitude}` :
                  t('Location Unknown')
                }
              </Typography>
            </Box>
          </Box>

          {/* Certifications section - always rendered */}
          <Box sx={styles.certificationSection}>
            <Typography sx={styles.certificationLabel}>{t('Certifications')}:</Typography>
            <Box sx={styles.certificationTags}>
              {driver.safetyCertificates && driver.safetyCertificates.length > 0 ? (
                <>
                  {driver.safetyCertificates.slice(0, 3).map((cert: string, index: number) => (
                    <Chip
                      key={index}
                      label={cert}
                      size="small"
                      sx={styles.certificationChip}
                      icon={<VerifiedIcon />}
                    />
                  ))}
                  {driver.safetyCertificates.length > 3 && (
                    <Typography sx={styles.moreCertifications}>
                      +{driver.safetyCertificates.length - 3} {t('more')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={styles.noData}>
                  {t('No certifications')}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Languages section - always rendered */}
          <Box sx={styles.languageSection}>
            <Typography sx={styles.languageLabel}>{t('Languages')}:</Typography>
            <Box sx={styles.languageTags}>
              {driver.knownLanguages && driver.knownLanguages.length > 0 ? (
                <>
                  {driver.knownLanguages.slice(0, 3).map((langId: number, index: number) => (
                    <Chip
                      key={index}
                      label={getLanguageFlag(langId)}
                      size="small"
                      sx={styles.languageChip}
                    />
                  ))}
                  {driver.knownLanguages.length > 3 && (
                    <Typography sx={styles.moreLanguages}>
                      +{driver.knownLanguages.length - 3} {t('more')}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography sx={styles.noData}>
                  {t('No languages')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

const styles = {
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  cardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  modernDriverHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: { xs: 0.75, sm: 1 }
  },
  driverNameContainer: {
    flex: 1
  },
  modernDriverName: {
    fontWeight: 800,
    fontSize: (theme: any) => theme.typography.fontSizes.heading,
    color: 'text.black',
    letterSpacing: '-0.02em',
    mb: 0.125
  },
  driverId: {
    fontSize: (theme: any) => theme.typography.fontSizes.small,
    color: 'text.grey',
    fontWeight: 500
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.25,
    px: { xs: 0.75, sm: 0.75, md: 0.875, lg: 1 },
    py: { xs: 0.125, sm: 0.25 },
    borderRadius: 1.25,
    color: 'text.white',
    fontWeight: 600,
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
  },
  statusText: {
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    fontWeight: 700
  },
  locationSection: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 0.75, sm: 1 },
    mb: { xs: 0.75, sm: 1, md: 1 },
    p: { xs: 1, sm: 1.25 },
    backgroundColor: 'background.lightGrey',
    borderRadius: 1.5,
    border: '1px solid border.light'
  },
  locationIcon: {
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    color: 'text.grey',
    flexShrink: 0
  },
  locationInfo: {
    flex: 1,
    minWidth: 0
  },
  locationText: {
    fontWeight: 600,
    color: 'text.black',
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    mb: 0.125
  },
  locationTime: {
    fontSize: (theme: any) => theme.typography.fontSizes.small,
    color: 'text.grey'
  },
  certificationSection: {
    mb: { xs: 0.75, sm: 1, md: 1 },
    p: { xs: 1, sm: 1.25 },
    backgroundColor: 'background.lightGrey',
    borderRadius: 1.5,
    border: '1px solid border.light'
  },
  certificationLabel: {
    fontSize: (theme: any) => theme.typography.fontSizes.small,
    color: 'text.grey',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    mb: 0.5
  },
  certificationTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5
  },
  certificationChip: {
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    height: 20,
    backgroundColor: (theme: any) => theme.palette.background.blueLight,
    color: (theme: any) => theme.palette.background.primaryBlue || theme.palette.border.blue,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.background.blueMedium
    }
  },
  moreCertifications: {
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    color: 'text.grey',
    fontWeight: 500,
    alignSelf: 'center'
  },
  languageSection: {
    mb: { xs: 0.75, sm: 1, md: 1 },
    p: { xs: 1, sm: 1.25 },
    backgroundColor: 'background.lightGrey',
    borderRadius: 1.5,
    border: '1px solid border.light'
  },
  languageLabel: {
    fontSize: (theme: any) => theme.typography.fontSizes.small,
    color: 'text.grey',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    mb: 0.5
  },
  languageTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5
  },
  languageChip: {
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    height: 20,
    backgroundColor: (theme: any) => theme.palette.background.orangeLight,
    color: (theme: any) => theme.palette.background.orange,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.background.orangeDark
    }
  },
  moreLanguages: {
    fontSize: (theme: any) => theme.typography.fontSizes.caption,
    color: 'text.grey',
    fontWeight: 500,
    alignSelf: 'center'
  },
  noData: {
    fontSize: (theme: any) => theme.typography.fontSizes.small,
    color: 'text.grey',
    fontStyle: 'italic',
    opacity: 0.7
  }
};

export default DriverCard;
